import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ApiError, api, apiErrorMessage } from '../lib/api';
import Pagination from '../components/Pagination';

type SortKey = 'publishedAt' | 'views';

const PAGE_SIZE = 20;

/**
 * 발행된 글 목록 — **GitHub repo `content/posts/`가 단일 진실 공급원**.
 * 즉 공개 사이트에 노출 중인 글이 곧 여기 목록 (D1 published row와 분리).
 *
 * "삭제(unpublish)" = GitHub 마크다운 삭제 + (있다면) D1 row 정리.
 * Cloudflare Pages가 자동 재빌드하면서 ~1분 후 공개 사이트에서도 빠진다.
 *
 * 5.15: Cloudflare Web Analytics 기반 slug별 조회수 표시 (7일 누적).
 *       발행순/조회수순 정렬. 분석 미설정/실패 시 조회수는 `—`.
 *       무료 zone analytics 보존이 ~7일이라 30d 토글은 미표시 (plan 시 확장).
 */
export default function Published() {
  const queryClient = useQueryClient();
  const [sortKey, setSortKey] = useState<SortKey>('publishedAt');

  const published = useQuery({
    queryKey: ['published'],
    queryFn: () => api.listPublished(),
  });

  const analytics = useQuery({
    queryKey: ['analytics-views', '7d'],
    queryFn: () => api.listAnalyticsViews('7d'),
    refetchInterval: 30_000, // 30s polling
    retry: false, // 503/502는 재시도 의미 X
  });

  const viewsBySlug = useMemo(() => {
    const m = new Map<string, number>();
    if (analytics.data) {
      for (const item of analytics.data.items) m.set(item.slug, item.views);
    }
    return m;
  }, [analytics.data]);

  const sortedItems = useMemo(() => {
    if (!published.data) return [];
    const items = [...published.data.items];
    if (sortKey === 'views') {
      items.sort((a, b) => (viewsBySlug.get(b.slug) ?? 0) - (viewsBySlug.get(a.slug) ?? 0));
    } else {
      items.sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
    }
    return items;
  }, [published.data, sortKey, viewsBySlug]);

  const [page, setPage] = useState(1);
  const [prevSortKey, setPrevSortKey] = useState(sortKey);
  if (prevSortKey !== sortKey) {
    setPrevSortKey(sortKey);
    setPage(1);
  }
  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const currentPage = page > totalPages ? 1 : page;
  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, currentPage]);

  const unpublishMutation = useMutation({
    mutationFn: (slug: string) => api.unpublish(slug),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['published'] });
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('발행 취소 완료', {
        description: `${res.slug} (커밋 ${res.commitSha.slice(0, 7)}) — 공개 사이트는 ~1분 후 반영`,
      });
    },
    onError: (err) => {
      toast.error('발행 취소 실패', { description: apiErrorMessage(err) });
    },
  });

  const analyticsUnavailable =
    analytics.isError && analytics.error instanceof ApiError && analytics.error.status === 503;

  return (
    <div>
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Published</h1>
        <p className="text-xs text-slate-500">
          GitHub repo가 진실 — 공개 사이트에 노출 중인 글
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <SortToggle value={sortKey} onChange={setSortKey} />
        <span className="text-xs text-slate-500">조회수 = 7일 누적</span>
        {analyticsUnavailable && (
          <span className="text-xs text-slate-500">
            (조회수 미설정 — Worker secret `CF_ANALYTICS_TOKEN` + var `CF_ZONE_TAG` 필요)
          </span>
        )}
      </div>

      {published.isLoading && <p className="text-slate-500">불러오는 중…</p>}
      {published.error && (
        <p className="text-red-600">
          오류:{' '}
          {published.error instanceof ApiError
            ? published.error.message
            : String(published.error)}
        </p>
      )}
      {published.data && published.data.items.length === 0 && (
        <p className="text-slate-500">발행된 글이 없습니다.</p>
      )}

      <ul className="space-y-2">
        {pagedItems.map((item) => {
          const isDeleting =
            unpublishMutation.isPending && unpublishMutation.variables === item.slug;
          const views = viewsBySlug.get(item.slug);
          return (
            <li
              key={item.slug}
              className="flex items-start justify-between gap-3 rounded border bg-white p-3 shadow-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{item.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                  <code className="rounded bg-slate-100 px-1.5 py-0.5">{item.slug}</code>
                  <span>발행 {new Date(item.publishedAt).toLocaleString('ko-KR')}</span>
                  <ViewsBadge views={views} unavailable={analyticsUnavailable} loading={analytics.isLoading} />
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Link
                  to={`/published/${encodeURIComponent(item.slug)}/edit`}
                  className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                >
                  편집
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      confirm(
                        `"${item.title}" 발행을 취소할까요?\n\nGitHub repo 마크다운 삭제 + 공개 사이트에서도 ~1분 후 빠집니다.`,
                      )
                    ) {
                      unpublishMutation.mutate(item.slug);
                    }
                  }}
                  disabled={isDeleting}
                  className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {isDeleting ? '취소 중…' : '발행 취소'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

function SortToggle({
  value,
  onChange,
}: {
  value: SortKey;
  onChange: (v: SortKey) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 text-xs text-slate-600">
      <span>정렬</span>
      <div className="inline-flex rounded border bg-white">
        {(
          [
            { k: 'publishedAt', label: '발행순' },
            { k: 'views', label: '조회수순' },
          ] as const
        ).map((opt) => (
          <button
            key={opt.k}
            type="button"
            onClick={() => onChange(opt.k)}
            className={`px-3 py-1 first:rounded-l last:rounded-r ${
              value === opt.k
                ? 'bg-slate-900 text-white'
                : 'hover:bg-slate-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ViewsBadge({
  views,
  unavailable,
  loading,
}: {
  views: number | undefined;
  unavailable: boolean;
  loading: boolean;
}) {
  if (unavailable) return <span className="text-slate-400">조회수 —</span>;
  if (loading && views === undefined) return <span className="text-slate-400">조회수 …</span>;
  return (
    <span className="font-mono">
      조회수 {(views ?? 0).toLocaleString('ko-KR')}
    </span>
  );
}
